/**
 * LocationTopicPage — /l/{...slug}/topic/{section-key}
 *
 * Renders a SINGLE section of a location's long-form article as its
 * own standalone page. Same data source as LocationArticle.jsx — we
 * just slice the `sections[<key>]` field and wrap it with breadcrumbs
 * + a "back to full article" link.
 *
 * Why this exists
 * ───────────────
 * The full location article carries 48 possible sections (Overview,
 * History, Geography, Economy, …) and reading it inline is heavy on
 * mobile. This route gives every topic a permalink so:
 *   • The TOC ("On this page") can hyperlink to standalone pages
 *   • Search engines can index each topic separately
 *   • Users can share e.g. "Portland → History" alone
 * The full-article page still keeps the in-page bookmark anchors, so
 * both UX modes coexist.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, BookOpenText, Loader2, MapPin } from "lucide-react";
import Linkify from "@/components/Linkify";
import EntityMessageBoard from "@/components/EntityMessageBoard";
import AiTopicContent from "@/components/AiTopicContent";
import useAiTopic from "@/hooks/useAiTopic";

const API = process.env.REACT_APP_BACKEND_URL;

// Must mirror LocationArticle's SECTION_LABELS map. Kept here (small
// dictionary) to avoid circular exports; add a new entry in both
// places when introducing a new section.
export const TOPIC_LABELS = {
  basic:           "Basic information",
  overview:        "Overview",
  history:         "History",
  geography:       "Geography",
  topography:      "Topography",
  weather:         "Weather & climate",
  population:      "Population",
  government:      "Government",
  economy:         "Economy",
  industries:      "Industries",
  agriculture:     "Agriculture",
  housing:         "Housing",
  neighborhoods:   "Neighborhoods",
  transportation:  "Transportation",
  infrastructure:  "Infrastructure",
  education:       "Education",
  healthcare:      "Healthcare",
  emergency:       "Emergency services",
  businesses:      "Businesses",
  restaurants:     "Restaurants & food",
  tourism:         "Tourism",
  recreation:      "Recreation",
  parks_nature:    "Parks & nature",
  waterways:       "Rivers, lakes & waterways",
  arts_culture:    "Arts & culture",
  religion:        "Religion",
  sports:          "Sports",
  events:          "Events",
  media:           "Media",
  personalities:   "Prominent personalities",
  companies:       "Notable companies",
  sister_cities:   "Sister cities",
  local_orgs:      "Local organisations",
  education_stats: "Education statistics",
  utilities:       "Public utilities",
  safety:          "Safety",
  environment:     "Environment",
  future:          "Future development",
  demographics:    "Demographics",
  facts:           "Interesting facts",
  faq:             "FAQ",
  images:          "Images",
  videos:          "Videos",
  maps:            "Maps",
  community:       "Social Pulse community",
  references:      "References",
};

const paletteFromKind = (kind) => {
  const map = {
    country: { from: "#F1F5F9", to: "#E2E8F0", accent: "#0F172A", ink: "#0F172A" },
    state:   { from: "#FEF3C7", to: "#FDE68A", accent: "#B45309", ink: "#7C2D12" },
    city:    { from: "#FEE2E2", to: "#FECACA", accent: "#B91C1C", ink: "#7F1D1D" },
    town:    { from: "#DBEAFE", to: "#BFDBFE", accent: "#1D4ED8", ink: "#1E3A8A" },
    village: { from: "#DCFCE7", to: "#BBF7D0", accent: "#15803D", ink: "#14532D" },
  };
  return map[(kind || "").toLowerCase()] || map.city;
};

export default function LocationTopicPage() {
  const params = useParams();
  const rest = params["*"] || "";
  // /l/<slug…>/topic/<key> — split on `/topic/`
  const idx = rest.lastIndexOf("/topic/");
  const slug  = idx >= 0 ? rest.slice(0, idx) : "";
  const key   = idx >= 0 ? rest.slice(idx + "/topic/".length) : "";

  const [doc, setDoc]     = useState(null);
  const [loading, setLoad] = useState(true);
  const [err, setErr]     = useState(null);

  useEffect(() => {
    let alive = true;
    if (!slug) return;
    axios.get(`${API}/api/locations/${slug}`)
      .then((r) => alive && setDoc(r.data))
      .catch((e) => alive && setErr(e?.response?.status || "err"))
      .finally(() => alive && setLoad(false));
    return () => { alive = false; };
  }, [slug]);

  const palette = useMemo(() => paletteFromKind(doc?.kind), [doc?.kind]);
  const label   = TOPIC_LABELS[key] || key.replaceAll("_", " ");
  const content = (doc?.sections || {})[key];

  // Auto-generate the topic content when the location's own article
  // doesn't have this section yet. Uses (source=location, entity_key=<slug>|<key>)
  // so the same location+topic pair is stable across refreshes.
  const ai = useAiTopic({
    source:    "location",
    entityKey: slug && key ? `${slug}|${key}` : "",
    title:     doc ? `${doc.name} · ${label}` : label,
    context:   doc ? `Location: ${doc.name}. Topic section: ${label}. ${doc.kind || "place"} in ${[doc.region, doc.country].filter(Boolean).join(", ")}.` : null,
    deepLink:  slug && key ? `/l/${slug}/topic/${key}` : null,
    enabled:   !!doc && !content,
  });

  if (loading) {
    return (
      <div className="my-16 text-center" data-testid="topic-loading">
        <Loader2 className="w-6 h-6 animate-spin mx-auto opacity-60" />
      </div>
    );
  }
  if (err || !doc) {
    return (
      <div className="my-16 text-center text-sm opacity-70" data-testid="topic-not-found">
        Article not found.
      </div>
    );
  }

  const parentHref = `/l/${slug}`;
  return (
    <main className="max-w-3xl mx-auto px-5 lg:px-8 py-8" data-testid={`location-topic-${key}`}>
      <nav className="text-xs opacity-70 mb-4" aria-label="Breadcrumb">
        <Link to="/" className="hover:underline">Home</Link>
        <span className="mx-2">/</span>
        <Link to={parentHref} className="hover:underline">{doc.name}</Link>
        <span className="mx-2">/</span>
        <span>{label}</span>
      </nav>

      <div
        className="rounded-2xl p-6 border mb-6"
        style={{ borderColor: `${palette.accent}33`, background: `linear-gradient(135deg, ${palette.from} 0%, ${palette.to} 100%)` }}
      >
        <div className="text-xs font-mono uppercase tracking-widest mb-2 flex items-center gap-1" style={{ color: palette.ink }}>
          <BookOpenText className="w-3 h-3" />
          <Link to={parentHref} className="hover:underline">{doc.name}</Link>
          <span className="mx-1 opacity-40">·</span>
          <span>Topic</span>
        </div>
        <h1
          className="font-display leading-[1.05] tracking-tight"
          style={{ color: palette.ink, fontSize: "clamp(2rem, 4.5vw, 3rem)" }}
          data-testid="topic-title"
        >
          {label}
        </h1>
        {doc.capital && (
          <div className="mt-3 text-sm inline-flex items-center gap-1" style={{ color: palette.ink }}>
            <MapPin className="w-4 h-4" /> Capital: <strong>{doc.capital}</strong>
          </div>
        )}
      </div>

      <article className="wofy-prose text-[15px] leading-relaxed" data-testid="topic-body">
        {content ? (
          String(content)
            .split(/\n{2,}/)
            .filter((p) => p.trim())
            .map((p, i) => (
              <p key={i} className="mb-4">
                <Linkify text={p.trim()} />
              </p>
            ))
        ) : (
          <AiTopicContent
            doc={ai.doc}
            status={ai.status}
            retry={ai.retry}
            title={`${doc.name} · ${label}`}
          />
        )}
      </article>

      <div className="mt-10 pt-6 border-t" style={{ borderColor: "var(--wofy-border)" }}>
        <Link
          to={parentHref}
          className="inline-flex items-center gap-2 text-sm font-semibold hover:underline"
          data-testid="topic-back-full-article"
          style={{ color: palette.accent }}
        >
          <ArrowLeft className="w-4 h-4" /> Back to the full {doc.name} article
        </Link>
      </div>

      {/* Discussion — every AI-generated topic page gets its own
          Facebook-style board so readers can add corrections /
          suggestions / photos / videos for that specific topic. */}
      <EntityMessageBoard
        entityType="topic"
        entityId={`${slug}/${key}`}
        title={`Discuss ${doc.name} · ${label}`}
      />
    </main>
  );
}
